import React, { useState } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { PlayerHandler, PlayerHeaderInfo } from '../PlayerHandler';

interface ForceUsernameModalProps {
  header: PlayerHeaderInfo;
  onClose: () => void;
  onSuccess: () => void;
}

const ForceUsernameModal: React.FC<ForceUsernameModalProps> = ({ header, onClose, onSuccess }) => {
  const { theme } = useTheme();
  const [newUsername, setNewUsername] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!header.userId) return;
    if (!newUsername.trim()) {
      setError('Username is required');
      return;
    }
    if (newUsername === header.username) {
      setError('New username must be different');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const handler = new PlayerHandler();
      const response = await handler.forceUsernameChange(header.userId, newUsername.trim(), reason);
      if (response?.success) {
        onSuccess();
      } else {
        setError('Failed to change username');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to change username');
    } finally {
      setLoading(false);
    }
  };

  const styles: Record<string, React.CSSProperties> = {
    overlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      background: theme.card,
      borderRadius: '12px',
      padding: '24px',
      width: '400px',
      maxWidth: '90vw',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
    title: { fontSize: '18px', fontWeight: 700, color: theme.text, marginBottom: '4px' },
    subtitle: { fontSize: '13px', color: theme.textSecondary, marginBottom: '20px' },
    label: { fontSize: '13px', fontWeight: 600, color: theme.text, marginBottom: '6px', display: 'block' },
    input: {
      width: '100%', padding: '10px 12px', fontSize: '14px',
      border: `1px solid ${theme.inputBorder}`, borderRadius: '8px',
      background: theme.inputBg, color: theme.text,
      outline: 'none', marginBottom: '16px', boxSizing: 'border-box' as const,
    },
    error: { fontSize: '13px', color: '#ef4444', marginBottom: '12px' },
    actions: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
    cancelBtn: {
      padding: '8px 20px', fontSize: '13px', fontWeight: 500,
      border: `1px solid ${theme.border}`, borderRadius: '8px',
      cursor: 'pointer', background: 'transparent', color: theme.textSecondary,
    },
    submitBtn: {
      padding: '8px 20px', fontSize: '13px', fontWeight: 600,
      border: 'none', borderRadius: '8px', cursor: 'pointer',
      background: '#3b82f6', color: '#fff', opacity: loading ? 0.6 : 1,
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.title}>Force Username Change</div>
        <div style={styles.subtitle}>Current: {header.username}</div>

        <label style={styles.label}>New Username</label>
        <input style={styles.input} value={newUsername} onChange={(e) => setNewUsername(e.target.value)} placeholder="Enter new username" />

        <label style={styles.label}>Reason</label>
        <input style={styles.input} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you changing this username?" />

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.actions}>
          <button style={styles.cancelBtn} onClick={onClose} disabled={loading}>Cancel</button>
          <button style={styles.submitBtn} onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : 'Change Username'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForceUsernameModal;
